/*
 * <pd-gallery-scroll-progress>
 *
 * Draws a real DOM scroll-progress bar for the mobile fullscreen product media
 * gallery. The gallery's "scrollbar" is otherwise a styled ::-webkit-scrollbar
 * (see frontend/legacy/src/styles/snippets/product-media-gallery.css), and iOS
 * Safari does not paint persistent custom-styled webkit scrollbars on touch
 * overflow containers — so the bar is invisible on iPhone. This element tracks
 * the slider's scrollLeft and sizes a thumb the same way, working on every
 * platform.
 *
 * Markup (rendered in snippets/product-media-gallery.liquid):
 *   <pd-gallery-scroll-progress data-slider-target="Slider-Gallery-…">
 *     <span class="product__media-progress-thumb"></span>
 *   </pd-gallery-scroll-progress>
 */
if (!customElements.get("pd-gallery-scroll-progress")) {
  customElements.define(
    "pd-gallery-scroll-progress",
    class GalleryScrollProgress extends HTMLElement {
      connectedCallback() {
        this.slider = document.getElementById(this.dataset.sliderTarget);
        this.thumb = this.querySelector(".product__media-progress-thumb");
        if (!this.slider || !this.thumb) return;

        this.ticking = false;
        this.onScroll = this.onScroll.bind(this);
        this.update = this.update.bind(this);

        this.slider.addEventListener("scroll", this.onScroll, { passive: true });
        this.resizeObserver = new ResizeObserver(this.update);
        this.resizeObserver.observe(this.slider);
        this.update();
      }

      disconnectedCallback() {
        if (this.slider) this.slider.removeEventListener("scroll", this.onScroll);
        if (this.resizeObserver) this.resizeObserver.disconnect();
      }

      onScroll() {
        // rAF-throttle so the scroll handler never blocks the main thread (INP).
        if (this.ticking) return;
        this.ticking = true;
        requestAnimationFrame(() => {
          this.update();
          this.ticking = false;
        });
      }

      update() {
        const { scrollWidth, clientWidth, scrollLeft } = this.slider;
        const scrollable = scrollWidth - clientWidth;

        // Nothing to scroll (single slide / not overflowing) → hide the bar.
        if (scrollable <= 1) {
          this.setAttribute("hidden", "");
          return;
        }
        this.removeAttribute("hidden");

        const ratio = clientWidth / scrollWidth; // thumb width as fraction of track
        const progress = scrollLeft / scrollable; // 0 → 1 across the scroll
        // Max travel, expressed relative to the thumb's own width (translateX %).
        const maxTranslate = ((1 - ratio) / ratio) * 100;

        this.thumb.style.width = `${ratio * 100}%`;
        this.thumb.style.transform = `translateX(${progress * maxTranslate}%)`;
      }
    },
  );
}
